import { STATELESS_FORM_KEY, type IControl, type IStatelessForm } from '@/composition/forms/types'
import {
  computed,
  inject,
  onMounted,
  onUnmounted,
  ref,
  watch,
  getCurrentInstance,
  type Ref,
  unref,
  isRef,
} from '@/composition/vue/compositionApi'
import { getValidationService } from '@/core/container/ioc'
import type {
  ValidationRule,
  ValidationRuleErrorWithRuleName,
  ValidationRuleFunction,
  ValidationRulesMap,
} from '@/services/validation/types'
import type { Dict } from '@/types'

let ID = 0

/**
 * Configuration options for creating a stateless form control.
 */
export type StatelessFormControlOptions = {
  /** Name of the form control. Used as key in form values object. If not provided, auto-generated name will be used. */
  name?: string | Ref<string>
  /** Model value of the control (plain value or Vue Ref). */
  modelValue?: Ref<unknown>
  /** Function to reset the control to its initial state. */
  reset: () => void
  /** Array of validation rules to apply to the control value. */
  validators?: ValidationRule[] | Ref<ValidationRule[]>
  /** Optional function to focus the control's input element. */
  focus?: () => void
  /** Validation mode for the control. */
  validationMode?: ('onchange' | 'onblur' | 'ondemand') | Ref<'onchange' | 'onblur' | 'ondemand'>
  /** Errors of the control. */
  errors?: ValidationRuleErrorWithRuleName[] | Ref<ValidationRuleErrorWithRuleName[]>
}

/**
 * Creates a stateless form control that integrates with the stateless form system.
 *
 * This composable creates a control that implements the IControl interface and automatically
 * registers/unregisters itself with the parent form. It handles validation, value management,
 * touched state, and focus management.
 *
 * @example
 * ```vue
 * <script setup>
 * import { ref } from 'vue'
 * import { useStatelessFormControl } from '@/composition/forms/useStatelessFormControl'
 *
 * const inputValue = ref('')
 * const inputRef = ref()
 *
 * const { control } = useStatelessFormControl({
 *   name: 'email',
 *   modelValue: inputValue,
 *   reset: () => { inputValue.value = '' },
 *   validators: [{ name: 'required' }, { name: 'email' }],
 *   focus: () => inputRef.value?.focus()
 * })
 *
 * const handleBlur = () => {
 *   control.handleBlur()
 * }
 *
 * </script>
 *
 * <template>
 *   <input
 *     ref="inputRef"
 *     v-model="inputValue"
 *     @blur="handleBlur"
 *   />
 *   <div v-if="control.shouldShowErrors()">
 *     <div v-for="error in control.getErrors()" :key="error.ruleName">
 *       {{ error.message }}
 *     </div>
 *   </div>
 * </template>
 * ```
 *
 * @param options - Configuration options for the control
 * @param options.name - Name of the control (used as key in form values)
 * @param options.modelValue - Current control value or a Vue Ref to it
 * @param options.reset - Function to reset control to initial state
 * @param options.validators - Array of validation rules
 * @param options.validationMode - When to validate ('onchange' | 'onblur' | 'ondemand')
 * @param options.errors - External errors merged with internal validation errors
 * @param options.focus - Optional function to focus the input element
 *
 * @returns Object containing:
 * - `form` - Reference to the parent form (null if not inside a form)
 * - `control` - Control object implementing IControl interface with methods:
 *   - `validate()` - Validates the control and returns boolean result
 *   - `getName()` - Returns control name (provided or auto-generated)
 *   - `getValue()` - Returns current control value
 *   - `getErrors()` - Returns array of validation errors
 *   - `getRulesResults()` - Returns validation rules results as key-value pairs
 *   - `reset()` - Resets control and clears touched state
 *   - `touch()` - Marks control as touched (user interacted with it)
 *   - `isTouched()` - Returns whether control has been touched
 *   - `shouldShowErrors()` - Returns true if errors should be displayed (touched && has errors)
 *   - `focus()` - Focuses the control's input element if focus function provided
 *
 * @remarks
 * - Control automatically registers with parent form on mount and unregisters on unmount
 * - If no parent form is found, control still works independently
 * - Validation uses the global validation service with support for both regular and local validators
 * - Touched state is managed internally and resets when control is reset
 * - Errors are only shown after control has been touched (following UX best practices)
 * - Auto-generated names follow pattern "stateless-form-control-{number}"
 *
 * @see {@link IControl} - Interface implemented by the returned control
 * @see {@link IStatelessForm} - Interface of the parent form
 * @see {@link useStatelessForm} - Composable for creating forms
 * @see {@link StatelessFormControlOptions} - Configuration options type
 *
 * @since 1.0.0
 */
export function useStatelessFormControl(options: StatelessFormControlOptions) {
  ID += 1
  const _id = ID
  const form = inject<IStatelessForm | null>(STATELESS_FORM_KEY, null)

  const instance = getCurrentInstance()
  if (!instance) {
    throw new Error('useStatelessFormControl must be called within setup()')
  }

  const name = computed(() => {
    const optionName = isRef(options.name) ? unref(options.name) : options.name
    return optionName ?? (instance.props.name as string) ?? undefined
  })
  const modelValue = computed(() => unref(options.modelValue) ?? instance.props.modelValue)
  const validators = computed(() =>
    isRef(options.validators)
      ? unref(options.validators)
      : (options.validators ?? (instance.props.validators as ValidationRule[]) ?? []),
  )
  const errors = computed(() =>
    isRef(options.errors)
      ? unref(options.errors)
      : (options.errors ?? (instance.props.errors as ValidationRuleErrorWithRuleName[]) ?? []),
  )
  const validationMode = computed(() =>
    isRef(options.validationMode)
      ? unref(options.validationMode)
      : (options.validationMode ?? (instance.props.validationMode as 'onchange' | 'onblur' | 'ondemand') ?? 'ondemand'),
  )

  const validationErrors = ref<ValidationRuleErrorWithRuleName[]>([])
  const rulesResultsBag = ref<Dict<boolean>>({})
  let isTouched = false
  let lastValidationRun = 0

  const errorsBag = computed<ValidationRuleErrorWithRuleName[]>(() => {
    const result = validationErrors.value ?? []
    return result.concat(errors.value)
  })

  const control: IControl = {
    async validate() {
      if (!validators.value || validators.value.length === 0) {
        validationErrors.value = []
        rulesResultsBag.value = {}
        return true
      }
      const localValidators: ValidationRulesMap = new Map()
      const customValidators = validators.value.filter((r) => r.customCallBack)
      if (customValidators.length > 0) {
        customValidators.forEach((r) => {
          localValidators.set(r.name, r.customCallBack as ValidationRuleFunction)
        })
      }
      const value = control.getValue()
      // eslint-disable-next-line no-plusplus
      const runId = ++lastValidationRun
      try {
        const {
          valid,
          errors: validationErrorsResult,
          rulesResults,
        } = await getValidationService().validate({
          value,
          validators: validators.value ?? [],
          formValues: (form?.getFormData() as Record<string, any>) ?? {},
          crossFieldValues: {},
          localValidators,
          validationInitiator: 'form',
        })
        if (runId !== lastValidationRun) {
          return false
        }
        validationErrors.value = validationErrorsResult
        rulesResultsBag.value = rulesResults
        return valid
      } catch (e) {
        if (runId !== lastValidationRun) {
          return false
        }
        validationErrors.value = []
        rulesResultsBag.value = {}
        return false
      }
    },
    getName() {
      return name.value ?? (form ? `${form.getId()}-${_id}` : `stateless-form-control-${_id}`)
    },
    getValue() {
      return modelValue.value
    },
    getErrors() {
      return errorsBag.value
    },
    resetErrors() {
      validationErrors.value = []
      rulesResultsBag.value = {}
    },
    getRulesResults() {
      return rulesResultsBag.value
    },
    reset() {
      options.reset()
      isTouched = false
    },
    touch: () => {
      isTouched = true
    },
    isTouched: () => {
      return isTouched
    },
    shouldShowErrors: () => {
      return isTouched && errorsBag.value.length > 0
    },
    focus: () => {
      options.focus?.()
    },
    handleBlur: () => {
      control.touch()
      if (validationMode.value === 'onblur') {
        control.validate()
      }
    },
  }
  onMounted(() => form?.register(control))
  onUnmounted(() => form?.unregister(control))

  watch(modelValue, () => {
    if (validationMode.value === 'onchange') {
      control?.validate()
    } else {
      control?.resetErrors()
    }
  })

  watch([validators, validationMode], () => {
    if (isTouched) {
      control?.validate()
    }
  })

  return {
    form,
    control,
  }
}
